import { useContext, useEffect, Suspense } from "react";

import { ApolloProvider } from "@apollo/client";
import { useLDClient } from "launchdarkly-react-client-sdk";
import { createBrowserRouter, Navigate } from "react-router-dom";

import { RVLoading } from "@regal-voice/ui";
import { AppLayout } from "App/Components/AppLayout/AppLayout";
import { UserContext } from "App/contexts";
import { ErrorBoundary } from "App/ErrorBoundary";
import NotFound from "App/NotFound";
import { getAllRoutes } from "App/routes";
import { routerBaseName } from "App/routes/navigation";
import { AppPagePaths } from "App/routes/paths";
import { VersionChecker } from "App/VersionChecker";
import { setLDClient } from "Application/thirdParty/launchDarkly";
import { UserNotifier } from "Components/elements";
import { TwilioDeviceProvider } from "Hooks/useTwilioDevice/useTwilioDevice";
import { CopilotSessionStreamProvider } from "Pages/copilot/CopilotSessionStreamContext/CopilotSessionStreamContext";
import { getApolloClient } from "Services/ApolloClientService";
import { BrandAdminSettingsLoader } from "Services/state/brand/BrandAdminSettings";
import { useAutocompleteListener } from "Services/state/hooks/useAutocompleteListener";
import { initializeConversations } from "Services/TwilioConversations.service";
import { interceptUnauthorizedResponses } from "Services/util/NetworkUtilService";
import { CollapsiblePanelsProvider } from "Utils/CollapsiblePanels";

import { AudioNotificationProvider } from "./AudioNotificationProvider/AudioNotificationProvider";
import { SecureChildren } from "./SecureRoute";
import SSEListeners from "./SSEListeners";
import { useFetchInterceptor } from "./useFetchInterceptor";
import { useOktaSessionIdleRefresh } from "./useOktaSessionIdleRefresh";
import UserProvider from "./UserProvider";
import { useSessionValidityCheck } from "./useSessionValidityCheck";
import useThirdPartyScripts from "./useThirdPartyScripts";

import type { AIAgentsFlags } from "Pages/agentBuilder/Pages/Routes";
import type { ConvoIntelFlags } from "Pages/convoIntel/ConvoIntelRoutes";
import type { LiveOpsFlags } from "Pages/liveOps/liveOpsRoutes";
import type { OrchestrationFlags } from "Pages/orchestration/orchestrationRoutes";
const ROUTES_WITH_UPDATE_NOTIFICATION_DISABLED = ["/reporting"];

function RootAppProviders({ children }: { children: React.ReactNode }) {
    useThirdPartyScripts();
    useAutocompleteListener();
    const ldClient = useLDClient();
    if (ldClient) {
        setLDClient(ldClient);
    }
    useFetchInterceptor(interceptUnauthorizedResponses);
    useSessionValidityCheck();
    useOktaSessionIdleRefresh();
    useEffect(() => {
        initializeConversations();
    }, []);
    return (
        <ApolloProvider client={getApolloClient()}>
            <CopilotSessionStreamProvider>
                <SSEListeners />
                <AudioNotificationProvider>
                    <UserNotifier />
                    <TwilioDeviceProvider>
                        <UserProvider>
                            <BrandAdminSettingsLoader />
                            <CollapsiblePanelsProvider>
                                <VersionChecker disabledOnRoutes={ROUTES_WITH_UPDATE_NOTIFICATION_DISABLED} />
                                <AppLayout pageReady>
                                    <ErrorBoundary>
                                        <Suspense fallback={<RVLoading />}>{children}</Suspense>
                                    </ErrorBoundary>
                                </AppLayout>
                            </CollapsiblePanelsProvider>
                        </UserProvider>
                    </TwilioDeviceProvider>
                </AudioNotificationProvider>
            </CopilotSessionStreamProvider>
        </ApolloProvider>
    );
}

// Role-based default route element
function RoleBasedDefaultRoute() {
    const { isAdmin } = useContext(UserContext);
    const defaultRoute = isAdmin ? AppPagePaths.regalLive : AppPagePaths.agentDesktop;
    return <Navigate to={defaultRoute} />;
}

type RouteConfig = {
    convoIntelPages: ConvoIntelFlags;
    liveOpsPages: LiveOpsFlags;
    orchestrationPages: OrchestrationFlags;
    aiAgentsPages: AIAgentsFlags;
};

const createRouteObject = ({ convoIntelPages, liveOpsPages, orchestrationPages, aiAgentsPages }: RouteConfig) => {
    const routesToUse = getAllRoutes({
        convoIntelPages,
        liveOpsPages,
        orchestrationPages,
        aiAgentsPages,
    });

    const routeObjects = [
        ...routesToUse.map(({ path, hasNestedRoutes, element, children }: any) => ({
            path: hasNestedRoutes ? `${path}/*` : path,
            element,
            children,
        })),
        {
            path: AppPagePaths.fallback,
            element: <RoleBasedDefaultRoute />,
        },
        {
            path: "*",
            element: <NotFound />,
        },
    ];
    return routeObjects;
};

export const configuredRouter = ({ convoIntelPages, liveOpsPages, orchestrationPages, aiAgentsPages }: RouteConfig) => {
    const routeObjects = createRouteObject({
        convoIntelPages,
        liveOpsPages,
        orchestrationPages,
        aiAgentsPages,
    });
    return createBrowserRouter(
        [
            {
                element: (
                    <RootAppProviders>
                        <SecureChildren />
                    </RootAppProviders>
                ),
                errorElement: <ErrorBoundary>Routing error occurred.</ErrorBoundary>,
                children: routeObjects,
            },
        ],
        {
            basename: `/${routerBaseName}`,
        }
    );
};
